import React, {
  ChangeEventHandler,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';
import { useHistory } from 'react-router-dom';
import {
  IconChevronDown,
  IconClose,
  IconFilter,
  IconFinance,
  IconGrid,
  IconLayers,
  IconList,
  IconPlus,
  IconSearch,
  IconSliders,
  IconUpload,
} from '../../assets/svg/icons';
import Button from '../../components/atoms/Button';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import routes from '../../setup/consts/routes';
import { FieldMode } from '../../components/atoms/Fields/enums';
import Input from '../../components/atoms/Fields/Input';
import Loader from '../../components/atoms/Loader';
import { LoaderMode } from '../../components/atoms/Loader/enums';
import WarehouseProductCardList from '../../components/molecules/WarehouseProductCardList';
import ScStat from '../../components/molecules/ScStat';
import ScPageHeader from '../../components/molecules/ScPageHeader';
import useHeaderTitleContext from '../../hooks/useHeaderTitleContext';
import { StyledAction, StyledForm, StyledProductListWrapper, StyledWarehouseStatGrid } from './styles';
import {
  StyledFilterChip,
  StyledFilterDropdownPanel,
  StyledToolbar,
  StyledToolbarActions,
  StyledToolbarFilters,
  StyledToolbarSearch,
} from '../../components/molecules/Toolbar';
import IconButton from '../../components/atoms/IconButton';
import useToggle from '../../hooks/useToggle';
import { useWarehouseProducts } from './hooks';
import Spinner from '../../components/atoms/Spinner';
import { SpinnerMode, SpinnerSize } from '../../components/atoms/Spinner/enums';
import { StyledClearButton } from '../../components/atoms/Fields/Autocomplete/styles';
import ScCard from '../../components/atoms/ScCard';
import { GetWarehouseProductsResponseSingleItem } from '../../setup/apiTypes/warehouse';


const WarehousePage: React.FC = () => {
  const { setHeaderTitle } = useHeaderTitleContext();
  const intl = useIntl();
  const history = useHistory();
  const [isHorizontalView, setIsHorizontalView] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedQuery] = useDebounce(searchQuery, 400);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [stockStatus, setStockStatus] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<string>('');

  const STOCK_STATUS_OPTIONS = [
    { value: '', label: intl.formatMessage({ id: 'warehouse.filterAllStock', defaultMessage: 'All stock' }) },
    { value: 'in', label: intl.formatMessage({ id: 'warehouse.filterInStock', defaultMessage: 'In stock (>5)' }) },
    { value: 'low', label: intl.formatMessage({ id: 'warehouse.filterLowStock', defaultMessage: 'Low stock (≤5)' }) },
    { value: 'out', label: intl.formatMessage({ id: 'warehouse.filterOutOfStock', defaultMessage: 'Out of stock' }) },
  ];

  const SORT_OPTIONS = [
    { value: '', label: intl.formatMessage({ id: 'warehouse.sortDefault', defaultMessage: 'Default order' }) },
    { value: 'name-asc', label: intl.formatMessage({ id: 'warehouse.sortNameAZ', defaultMessage: 'Name A → Z' }) },
    { value: 'name-desc', label: intl.formatMessage({ id: 'warehouse.sortNameZA', defaultMessage: 'Name Z → A' }) },
    { value: 'stock-asc', label: intl.formatMessage({ id: 'warehouse.sortStockAsc', defaultMessage: 'Stock ↑' }) },
    { value: 'stock-desc', label: intl.formatMessage({ id: 'warehouse.sortStockDesc', defaultMessage: 'Stock ↓' }) },
  ];

  const { items: rawItems, total: totalProducts, hasMore, isFetching, isLoading, fetchMore, refetch } =
    useWarehouseProducts({
      pageSize: 8,
      query: debouncedQuery,
    });

  const warehouseItems = rawItems as unknown as GetWarehouseProductsResponseSingleItem[];

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'mainNavigation.warehouse',
        defaultMessage: 'Warehouse',
      })
    );
  }, [intl, setHeaderTitle]);

  useEffect(() => {
    if (!debouncedQuery) refetch();
  }, [debouncedQuery, refetch]);

  const handleSearchQueryChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearchQuery = () => {
    setSearchQuery('');
  };

  const summary = useMemo(() => {
    const totals = warehouseItems.reduce(
      (acc, p) => {
        acc.skus += 1;
        acc.onHand += p.onHandQuantity ?? 0;
        acc.damaged += p.damagedQuantity ?? 0;
        if ((p.onHandQuantity ?? 0) === 0 && (p.orderedQuantity ?? 0) > 0) {
          acc.outOfStock += 1;
        } else if ((p.onHandQuantity ?? 0) > 0 && (p.onHandQuantity ?? 0) <= 5) {
          acc.lowStock += 1;
        }
        return acc;
      },
      { skus: 0, onHand: 0, damaged: 0, lowStock: 0, outOfStock: 0 }
    );
    return totals;
  }, [warehouseItems]);

  // Unique categories derived from loaded items
  const categoryOptions = useMemo(() => {
    const names = Array.from(
      new Set(warehouseItems.map((p) => p.categoryName).filter(Boolean))
    ).sort();
    return [
      { value: '', label: 'All categories' },
      ...names.map((n) => ({ value: n, label: n })),
    ];
  }, [warehouseItems]);

  // Apply filters + sort client-side
  const filteredItems = useMemo(() => {
    let result = warehouseItems.slice();

    if (categoryFilter) {
      result = result.filter((p) => p.categoryName === categoryFilter);
    }

    if (stockStatus === 'in') {
      result = result.filter((p) => (p.onHandQuantity ?? 0) > 5);
    } else if (stockStatus === 'low') {
      result = result.filter(
        (p) => (p.onHandQuantity ?? 0) > 0 && (p.onHandQuantity ?? 0) <= 5
      );
    } else if (stockStatus === 'out') {
      result = result.filter((p) => (p.onHandQuantity ?? 0) === 0);
    }

    if (sortOrder === 'name-asc') {
      result = result.slice().sort((a, b) =>
        (a.productName ?? '').localeCompare(b.productName ?? '')
      );
    } else if (sortOrder === 'name-desc') {
      result = result.slice().sort((a, b) =>
        (b.productName ?? '').localeCompare(a.productName ?? '')
      );
    } else if (sortOrder === 'stock-asc') {
      result = result.slice().sort(
        (a, b) => (a.onHandQuantity ?? 0) - (b.onHandQuantity ?? 0)
      );
    } else if (sortOrder === 'stock-desc') {
      result = result.slice().sort(
        (a, b) => (b.onHandQuantity ?? 0) - (a.onHandQuantity ?? 0)
      );
    }

    return result as unknown as typeof rawItems;
  }, [warehouseItems, categoryFilter, stockStatus, sortOrder, rawItems]);

  const [isCategoryOpen, { toggle: toggleCategory, toggleOff: closeCategoryDropdown, registerContainerRef: categoryContainerRef }] = useToggle();
  const [isStockOpen, { toggle: toggleStock, toggleOff: closeStockDropdown, registerContainerRef: stockContainerRef }] = useToggle();
  const [isSortOpen, { toggle: toggleSort, toggleOff: closeSortDropdown, registerContainerRef: sortContainerRef }] = useToggle();

  const hasActiveFilters = !!categoryFilter || !!stockStatus || !!sortOrder;

  const clearFilters = () => {
    setCategoryFilter('');
    setStockStatus('');
    setSortOrder('');
  };

  return (
    <>
      <ScPageHeader
        title={intl.formatMessage({ id: 'mainNavigation.warehouse', defaultMessage: 'Warehouse' })}
        subtitle={
          isLoading
            ? intl.formatMessage({ id: 'warehouse.loadingInventory', defaultMessage: 'Loading inventory…' })
            : intl.formatMessage({ id: 'warehouse.pageSubtitle', defaultMessage: 'Inventory across all entities · {count} SKUs on this page' }, { count: rawItems.length })
        }
        actions={
          <>
            <div style={{ display: 'flex', gap: 4, padding: 3, borderRadius: 'var(--r-md)', background: 'var(--paper-2)' }}>
              <button style={{
                padding: '7px 12px', borderRadius: 6, border: 0, fontFamily: 'inherit',
                background: 'var(--surface)', boxShadow: 'var(--sh-1)',
                color: 'var(--ink)', fontSize: 'var(--t-sm)', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <IconGrid style={{ width: 14, height: 14 }} />
                {intl.formatMessage({ id: 'warehouse.inventoryTab', defaultMessage: 'Inventory' })}
              </button>
              <button
                onClick={() => history.push(routes.REPORT)}
                style={{
                  padding: '7px 12px', borderRadius: 6, border: 0, fontFamily: 'inherit',
                  background: 'transparent', color: 'var(--muted)',
                  fontSize: 'var(--t-sm)', fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <IconFinance style={{ width: 14, height: 14 }} />
                {intl.formatMessage({ id: 'warehouse.analyticsTab', defaultMessage: 'Analytics' })}
              </button>
            </div>
            <Button kind={ButtonKind.Outline} icon={IconUpload}>
              {intl.formatMessage({ id: 'warehouse.importSkus', defaultMessage: 'Import SKUs' })}
            </Button>
            <Button kind={ButtonKind.Primary} icon={IconPlus}>
              {intl.formatMessage({ id: 'warehouse.addProduct', defaultMessage: 'Add product' })}
            </Button>
          </>
        }
      />
      <StyledWarehouseStatGrid>
        <ScStat
          label={intl.formatMessage({ id: 'warehouse.productsOnPage', defaultMessage: 'Products on page' })}
          value={summary.skus}
          sub={totalProducts > 0 ? intl.formatMessage({ id: 'warehouse.ofTotal', defaultMessage: 'of {total} total' }, { total: totalProducts.toLocaleString() }) : intl.formatMessage({ id: 'warehouse.acrossAllSkus', defaultMessage: 'Across all SKUs' })}
          accent='ink'
          isLoading={isLoading}
          showBorder
        />
        <ScStat
          label={intl.formatMessage({ id: 'warehouse.unitsOnHand', defaultMessage: 'Units on hand' })}
          value={summary.onHand.toLocaleString()}
          sub={intl.formatMessage({ id: 'warehouse.acrossAllSkus', defaultMessage: 'Across all SKUs' })}
          accent={summary.onHand > 0 ? 'green' : 'ink'}
          isLoading={isLoading}
          showBorder
        />
        <ScStat
          label={intl.formatMessage({ id: 'warehouse.lowStock', defaultMessage: 'Low stock (≤5)' })}
          value={summary.lowStock}
          sub={summary.lowStock > 0 ? intl.formatMessage({ id: 'warehouse.needsReorder', defaultMessage: 'Needs reorder soon' }) : 'Healthy'}
          accent={summary.lowStock > 0 ? 'warning' : 'muted'}
          isLoading={isLoading}
          showBorder
        />
        <ScStat
          label={intl.formatMessage({ id: 'warehouse.outOfStock', defaultMessage: 'Out of stock' })}
          value={summary.outOfStock}
          sub={summary.outOfStock > 0 ? intl.formatMessage({ id: 'warehouse.refillRequired', defaultMessage: 'Refill required' }) : intl.formatMessage({ id: 'warehouse.filterAllStock', defaultMessage: 'All stock' })}
          accent={summary.outOfStock > 0 ? 'danger' : 'muted'}
          isLoading={isLoading}
          showBorder
        />
        <ScStat
          label={intl.formatMessage({ id: 'warehouse.damagedUnits', defaultMessage: 'Damaged units' })}
          value={summary.damaged}
          sub={intl.formatMessage({ id: 'warehouse.pendingWriteoff', defaultMessage: 'Pending writeoff' })}
          accent={summary.damaged > 0 ? 'warning' : 'muted'}
          isLoading={isLoading}
          showBorder
        />
      </StyledWarehouseStatGrid>
      <ScCard pad={false} style={{ padding: 'var(--s-4)', marginBottom: 'var(--s-4)' }}>
      <StyledToolbar style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
        <StyledToolbarSearch>
          <StyledForm onSubmit={(e) => e.preventDefault()}>
            <Input
              mode={FieldMode.Tertiary}
              placeholder={intl.formatMessage({
                id: 'search.searchProducts',
                defaultMessage: 'Search products by name, SKU or category…',
              })}
              isRounded
              prefixComponent={<IconSearch />}
              suffixComponent={
                (isFetching && searchQuery) ||
                (debouncedQuery && !searchQuery) ? (
                  <Spinner
                    mode={SpinnerMode.Dark}
                    size={SpinnerSize.ExtraSmall}
                  />
                ) : debouncedQuery ? (
                  <StyledClearButton
                    type='button'
                    aria-label='Clear search'
                    onClick={clearSearchQuery}
                  >
                    <IconClose />
                  </StyledClearButton>
                ) : null
              }
              value={searchQuery}
              onChange={handleSearchQueryChange}
              type='input'
              disabled={isLoading || isFetching}
            />
          </StyledForm>
        </StyledToolbarSearch>

        <StyledToolbarFilters>
          {/* Category filter */}
          <div ref={categoryContainerRef} style={{ position: 'relative' }}>
            <StyledFilterChip type='button' onClick={toggleCategory} aria-expanded={isCategoryOpen}>
              <span className='fc-icon'><IconLayers /></span>
              <span className='fc-label'>{intl.formatMessage({ id: 'warehouse.filterCategory', defaultMessage: 'Category' })}</span>
              <span className='fc-badge'>{categoryFilter || intl.formatMessage({ id: 'warehouse.filterAll', defaultMessage: 'All' })}</span>
              <span className='fc-chevron'><IconChevronDown /></span>
            </StyledFilterChip>
            {isCategoryOpen && (
              <StyledFilterDropdownPanel>
                {categoryOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type='button'
                    aria-selected={categoryFilter === opt.value}
                    onClick={() => { setCategoryFilter(opt.value); closeCategoryDropdown(); }}
                  >
                    {opt.label}
                  </button>
                ))}
              </StyledFilterDropdownPanel>
            )}
          </div>

          {/* Stock filter */}
          <div ref={stockContainerRef} style={{ position: 'relative' }}>
            <StyledFilterChip type='button' onClick={toggleStock} aria-expanded={isStockOpen}>
              <span className='fc-icon'><IconFilter /></span>
              <span className='fc-label'>{intl.formatMessage({ id: 'warehouse.filterStock', defaultMessage: 'Stock' })}</span>
              <span className='fc-badge'>{STOCK_STATUS_OPTIONS.find((o) => o.value === stockStatus)?.label ?? intl.formatMessage({ id: 'warehouse.filterAll', defaultMessage: 'All' })}</span>
              <span className='fc-chevron'><IconChevronDown /></span>
            </StyledFilterChip>
            {isStockOpen && (
              <StyledFilterDropdownPanel>
                {STOCK_STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type='button'
                    aria-selected={stockStatus === opt.value}
                    onClick={() => { setStockStatus(opt.value); closeStockDropdown(); }}
                  >
                    {opt.label}
                  </button>
                ))}
              </StyledFilterDropdownPanel>
            )}
          </div>

          {/* Sort */}
          <div ref={sortContainerRef} style={{ position: 'relative' }}>
            <StyledFilterChip type='button' onClick={toggleSort} aria-expanded={isSortOpen}>
              <span className='fc-icon'><IconSliders /></span>
              <span className='fc-label'>{intl.formatMessage({ id: 'warehouse.sortLabel', defaultMessage: 'Sort' })}: {SORT_OPTIONS.find((o) => o.value === sortOrder)?.label ?? intl.formatMessage({ id: 'warehouse.sortDefault', defaultMessage: 'Default' })}</span>
              <span className='fc-chevron'><IconChevronDown /></span>
            </StyledFilterChip>
            {isSortOpen && (
              <StyledFilterDropdownPanel>
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type='button'
                    aria-selected={sortOrder === opt.value}
                    onClick={() => { setSortOrder(opt.value); closeSortDropdown(); }}
                  >
                    {opt.label}
                  </button>
                ))}
              </StyledFilterDropdownPanel>
            )}
          </div>

          {hasActiveFilters && (
            <button
              type='button'
              onClick={clearFilters}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px',
                fontFamily: 'inherit', fontSize: 'var(--t-sm)', fontWeight: 500,
                color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 4,
                transition: 'color var(--dur-fast) var(--ease-out)',
              }}
            >
              <IconClose style={{ width: 12, height: 12 }} /> Clear
            </button>
          )}
        </StyledToolbarFilters>

        <StyledToolbarActions>
          <StyledAction>
            <IconButton
              isSecondary
              isActive={!isHorizontalView}
              icon={IconGrid}
              aria-label='Grid view'
              onClick={() => setIsHorizontalView(false)}
            />
            <IconButton
              isSecondary
              isActive={isHorizontalView}
              icon={IconList}
              aria-label='List view'
              onClick={() => setIsHorizontalView(true)}
            />
          </StyledAction>
        </StyledToolbarActions>
      </StyledToolbar>
      </ScCard>
      <StyledProductListWrapper>
        {isLoading ? (
          <Loader
            mode={
              isHorizontalView
                ? LoaderMode.ProductOrderCardList
                : LoaderMode.WarehouseGridList
            }
          />
        ) : (
          <WarehouseProductCardList
            {...{ hasMore, fetchMoreData: fetchMore }}
            isHorizontal={isHorizontalView}
            items={filteredItems}
            refetch={refetch}
          />
        )}
      </StyledProductListWrapper>
    </>
  );
};

export default WarehousePage;
